/**
 * Paginação client-side da página /casos (E13.F5.T04).
 *
 * Receives total + page + size; renderiza:
 *   - sumário "{start}-{end} de {total}"
 *   - select de pageSize ∈ {25, 50, 100}
 *   - 4 botões: Primeira, Anterior, Próxima, Última
 * Stateless — o consumidor (`CasosPage`) mantém page/size em URLSearchParams.
 */
import { ptBR } from '@/i18n/pt-BR';

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export function isValidPageSize(value: number): value is PageSize {
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);
}

export function pageCount(total: number, size: number): number {
  if (total <= 0) return 1;
  return Math.max(1, Math.ceil(total / size));
}

export function clampPage(page: number, total: number, size: number): number {
  const max = pageCount(total, size);
  if (!Number.isFinite(page) || page < 1) return 1;
  if (page > max) return max;
  return Math.floor(page);
}

interface CasosPaginationProps {
  total: number;
  page: number;
  size: PageSize;
  onPageChange: (next: number) => void;
  onSizeChange: (next: PageSize) => void;
}

export function CasosPagination({
  total,
  page,
  size,
  onPageChange,
  onSizeChange,
}: CasosPaginationProps): JSX.Element {
  const pages = pageCount(total, size);
  const safePage = clampPage(page, total, size);
  const start = total === 0 ? 0 : (safePage - 1) * size + 1;
  const end = Math.min(total, safePage * size);
  const summary = ptBR.casos.pagination.summary
    .replace('{{start}}', String(start))
    .replace('{{end}}', String(end))
    .replace('{{total}}', String(total));

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-fg/[0.03] px-4 py-2 text-caption"
      data-testid="casos-pagination"
    >
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1 text-fg-muted">
          <span>{ptBR.casos.pagination.sizeLabel}</span>
          <select
            data-testid="casos-pagination-size"
            value={size}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (isValidPageSize(next)) onSizeChange(next);
            }}
            className="border border-border bg-surface px-2 py-1 text-fg"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
        <span className="text-fg-muted" data-testid="casos-pagination-summary">
          {summary}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="casos-pagination-first"
          aria-label={ptBR.casos.pagination.first}
          disabled={safePage === 1}
          onClick={() => onPageChange(1)}
          className="border border-border bg-transparent px-2 py-1 text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-40"
        >
          «
        </button>
        <button
          type="button"
          data-testid="casos-pagination-prev"
          aria-label={ptBR.casos.pagination.prev}
          disabled={safePage === 1}
          onClick={() => onPageChange(safePage - 1)}
          className="border border-border bg-transparent px-2 py-1 text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-40"
        >
          ‹
        </button>
        <span className="font-mono text-fg" data-testid="casos-pagination-current">
          {ptBR.casos.pagination.pageLabel} {safePage}/{pages}
        </span>
        <button
          type="button"
          data-testid="casos-pagination-next"
          aria-label={ptBR.casos.pagination.next}
          disabled={safePage >= pages}
          onClick={() => onPageChange(safePage + 1)}
          className="border border-border bg-transparent px-2 py-1 text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-40"
        >
          ›
        </button>
        <button
          type="button"
          data-testid="casos-pagination-last"
          aria-label={ptBR.casos.pagination.last}
          disabled={safePage >= pages}
          onClick={() => onPageChange(pages)}
          className="border border-border bg-transparent px-2 py-1 text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-40"
        >
          »
        </button>
      </div>
    </div>
  );
}
